import { useEffect, useRef, useState } from "react";
import { Play, Clock, UsersThree, CaretDown, ArrowLeft, YoutubeLogo, SpeakerSlash, MusicNotes } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { LUMI_POSES } from "@/data/lumiPoses";
import { EPISODES } from "@/data/feelingsEpisodes";
import { useKidsSound } from "@/hooks/useKidsSound";
import { stopLumiAudio, clearLumiReplay } from "@/lib/lumiAudio";
import { KidsVideoPlayer } from "./KidsVideoPlayer";
import { WelcomeSequence } from "./WelcomeSequence";
import { SingAlong } from "./SingAlong";
import { useLumi } from "./LumiCharacter";

const PANEL = "bg-white rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)]";
const THUMB_ASPECT = { aspectRatio: "2000 / 953" }; // Kids_adventure image proportions — Mom, Lumi and the girl always in frame

const EpisodeCard = ({ ep }) => {
  const [watching, setWatching] = useState(false);
  const fullEpisode = ep.status === "ready" && ep.youtubeId;
  return (
    <article className={`${PANEL} overflow-hidden grid md:grid-cols-5`} data-testid={`episode-card-${ep.id}`}>
      <div className="md:col-span-2 bg-[#efe9fb] relative flex items-center">
        {watching && ep.sneakPeek ? (
          <KidsVideoPlayer
            src={ep.sneakPeek.src}
            poster={ep.sneakPeek.poster}
            captions={ep.sneakPeek.captions}
            title={`${ep.title} — musical sneak peek`}
            playLabel={ep.sneakPeek.title}
            autoPlay
            testid={`sneak-peek-${ep.id}`}
          />
        ) : ep.thumbnail ? (
          <img src={ep.thumbnail} alt={`${ep.title}: Mom, Lumi and a girl on a sunny sidewalk.`} className="w-full h-auto object-contain" style={THUMB_ASPECT} loading="lazy" data-testid={`episode-thumb-${ep.id}`} />
        ) : (
          <img src={LUMI_POSES.reading} alt="" className="h-40 mx-auto object-contain drop-shadow-md" />
        )}
      </div>
      <div className="md:col-span-3 p-6 sm:p-8 flex flex-col justify-center gap-3">
        <h3 className="font-nunito font-extrabold text-xl sm:text-2xl text-[#4a2e8a]">{ep.title}</h3>
        <p className="font-nunito text-base text-[#5b5572] leading-relaxed">{ep.description}</p>
        <div className="flex flex-wrap items-center gap-3">
          {ep.sneakPeek && !watching && (
            <Button onClick={() => setWatching(true)} className="w-fit bg-[#6a3fc7] hover:bg-[#5a33ad] text-white font-nunito font-bold rounded-full px-6 py-5 border-0 focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid={`episode-sneak-peek-${ep.id}`}>
              <MusicNotes size={20} weight="fill" className="mr-2" /> {ep.sneakPeek.title}
            </Button>
          )}
          {fullEpisode && (
            <Button asChild className="w-fit bg-[#2fb59f] hover:bg-[#27997f] text-white font-nunito font-bold rounded-full px-6 py-5 border-0">
              <a href={`https://www.youtube-nocookie.com/embed/${ep.youtubeId}`} target="_blank" rel="noopener noreferrer" data-testid={`episode-watch-${ep.id}`}><YoutubeLogo size={20} weight="fill" className="mr-2" /> Watch Episode</a>
            </Button>
          )}
          {!fullEpisode && (
            <span className="inline-flex items-center gap-2 w-fit bg-[#efe9fb] text-[#6a3fc7] font-nunito font-bold text-sm rounded-full px-4 py-2" data-testid={`episode-coming-soon-${ep.id}`}>
              <Clock size={16} weight="bold" /> {ep.statusLabel || "Coming soon"}
            </span>
          )}
        </div>
        {watching && ep.sneakPeek && !ep.sneakPeek.captions && (
          <p className="font-nunito text-xs text-[#7a748f]" data-testid={`sneak-peek-captions-note-${ep.id}`}>Captions for this song are on the way.</p>
        )}
      </div>
    </article>
  );
};

export const FeelingsStation = ({ onBack }) => {
  const [soundOn] = useKidsSound();
  const [parentsOpen, setParentsOpen] = useState(false);
  const lumi = useLumi();
  const welcomeRef = useRef(null);

  // Calm page: no floating companion or bubbles; all sound stops when leaving the section
  useEffect(() => {
    lumi.idle();
    lumi.hide();
    return () => { stopLumiAudio(); clearLumiReplay(); lumi.show(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="max-w-6xl mx-auto px-4 pb-10" data-testid="feelings-station">
      <header className="mb-8 text-center">
        <h2 className="font-nunito font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#4a2e8a] tracking-tight inline-flex items-center justify-center gap-3" data-testid="feelings-heading">
          Feelings Station <span aria-hidden="true" className="text-[#f2b544] text-2xl sm:text-3xl">✦</span>
        </h2>
        <p className="font-nunito text-base md:text-lg text-[#5b5572] mt-2" data-testid="feelings-subtitle">Stories, songs, and everyday adventures with Lumi.</p>
      </header>

      <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
        <section className={`${PANEL} lg:col-span-2 p-6 sm:p-8 relative overflow-hidden flex items-center`} aria-labelledby="welcome-title" data-testid="feelings-welcome-panel">
          <span aria-hidden="true" className="absolute top-4 right-5 text-[#f48fb1] text-lg">✦</span>
          <span aria-hidden="true" className="absolute bottom-5 right-1/2 text-[#f2b544] text-sm">✦</span>
          <div className="grid grid-cols-[1fr_auto] gap-x-4 sm:gap-x-6 gap-y-5 items-center w-full">
            <div className="min-w-0">
              <h2 id="welcome-title" className="font-nunito font-extrabold text-3xl sm:text-4xl text-[#4a2e8a]">Welcome aboard!</h2>
              <p className="font-nunito text-base sm:text-lg text-[#5b5572] mt-3 leading-relaxed">Explore feelings, understand words, and practice asking for help.</p>
            </div>
            <button
              type="button"
              onClick={() => welcomeRef.current?.playWelcome()}
              aria-label="Play the Welcome to Feelings Station video"
              className="sm:row-span-2 self-center -mr-2 sm:-mr-4 rounded-[2rem] focus-visible:ring-4 focus-visible:ring-[#b79cf0] hover:scale-[1.03] transition-transform"
              data-testid="feelings-welcome-lumi"
            >
              <img src={LUMI_POSES.waving} alt="" className="h-40 sm:h-56 lg:h-60 w-auto max-w-[38vw] sm:max-w-none object-contain drop-shadow-[0_10px_24px_rgba(106,63,199,0.25)] select-none" draggable={false} />
            </button>
            <div className="col-span-2 sm:col-span-1 min-w-0">
              <Button onClick={() => welcomeRef.current?.playWelcome()} className="w-fit max-w-full bg-[#6a3fc7] hover:bg-[#5a33ad] text-white font-nunito font-bold text-sm sm:text-base rounded-2xl px-4 py-5 border-0 focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid="play-welcome-btn">
                <Play size={18} weight="fill" className="mr-1.5" /> Play welcome
              </Button>
              <p className="mt-3 font-nunito text-sm text-[#7a748f]">Tip: tap Lumi to play the welcome video.</p>
              {!soundOn && (
                <p className="mt-2 inline-flex items-center gap-1.5 font-nunito text-sm text-[#6a3fc7]" data-testid="sound-off-note">
                  <SpeakerSlash size={16} weight="bold" /> Sound is off — captions stay on screen.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className={`${PANEL} lg:col-span-3 overflow-hidden flex flex-col`} aria-label="Welcome videos" data-testid="feelings-welcome-videos">
          <WelcomeSequence ref={welcomeRef} />
        </section>
      </div>

      <SingAlong
        videoId="dS_ak_n7r4E"
        title="Feelings Parade"
        description="Explore happy, sad, mad, scared, and calm feelings with Lumi. Sing, clap, sway, or simply watch—every feeling is welcome!"
      />

      <section className="mt-12" aria-labelledby="episodes-title">
        <h2 id="episodes-title" className="font-nunito font-extrabold text-2xl sm:text-3xl text-[#4a2e8a] mb-5">Our first adventure <span aria-hidden="true" className="text-[#f2b544] text-xl">✦</span></h2>
        <div className="space-y-4" data-testid="episodes-list">
          {EPISODES.map((ep) => <EpisodeCard key={ep.id} ep={ep} />)}
        </div>
      </section>

      <section className={`${PANEL} mt-8`} data-testid="parents-panel">
        <button
          type="button"
          onClick={() => setParentsOpen((o) => !o)}
          aria-expanded={parentsOpen}
          aria-controls="parents-panel-body"
          className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left rounded-3xl focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
          data-testid="parents-panel-toggle"
        >
          <span className="inline-flex items-center gap-3 font-nunito font-bold text-base text-[#4a2e8a]"><UsersThree size={22} weight="duotone" /> For parents and caregivers</span>
          <CaretDown size={20} weight="bold" className={`text-[#6a3fc7] transition-transform ${parentsOpen ? "rotate-180" : ""}`} />
        </button>
        {parentsOpen && (
          <p id="parents-panel-body" className="px-6 pb-6 font-nunito text-base text-[#5b5572] leading-relaxed" data-testid="parents-panel-body">
            Watch together at your child's pace. Pause or replay whenever helpful. After an episode, talk about what happened and practice one useful phrase together.
          </p>
        )}
      </section>

      <button type="button" onClick={onBack} className="mt-8 inline-flex items-center gap-2 font-nunito font-bold text-base text-[#4a2e8a] hover:underline rounded-full px-3 py-2 focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid="feelings-back-btn">
        <ArrowLeft size={18} weight="bold" /> Back to Kids Corner
      </button>
    </div>
  );
};

export default FeelingsStation;
